<template>
  <SectionCard title="Objekt">
    <div class="card-columns">
      <DataSection title="Objekt">
        <DataField label="Adress">{{ fullAddress }}</DataField>
        <DataField label="Säkerhet">{{ collateralLabel(debt.collateralProperty) }}</DataField>
        <DataField label="Typ av objekt">{{ t(`PROPERTY_TYPE.${debt.collateralProperty.type}`) }}</DataField>
      </DataSection>

      <!-- Värdering -->
      <DataSection title="Värdering">
        <DataField label="Aktuell värdering">
          {{ formatAmountWithCurrency(debt.collateralProperty.valuations.currentValuation?.value) }}
        </DataField>
        <DataField label="Total belåning">{{ formatAmountWithCurrency(debt.totalCurrentPrincipal) }}</DataField>
        <DataField label="Belåningsgrad">{{ formatPercentage(debt.ltv * 100) }}</DataField>
        <DataField label="Värderingsdatum">
          {{ formatDateIfValid(debt.collateralProperty.valuations.currentValuation?.valuationDate) ?? '-' }}
        </DataField>
        <DataField label="Energideklaration">{{ energyDeclarationLabel }}</DataField>
      </DataSection>

      <!-- Amortering -->
      <DataSection title="Amortering">
        <DataField label="Aktuell amortering">
          <span :class="{ 'is-superseded': amortizationIsPaused || pendingTotalAmortization !== null }">
            {{ formatAmount(totalAmortization) }} kr/mån
          </span>
          <span v-if="pendingTotalAmortization !== null" class="text-subtle">
            {{ pendingAmortizationNote(pendingTotalAmortization) }}
          </span>
          <span v-if="amortizationIsPaused" class="paused-note">
            Amortering pausad till {{ formatDateIfValid(debt.servicingSettings.pauseAmortizationUntil) ?? '-' }}
          </span>
        </DataField>
        <DataField label="Amorteringskrav">
          {{ formatAmount(debt.servicingSettings.minAmortization ?? 0) }} kr/mån
        </DataField>
      </DataSection>

      <!-- Amorteringsunderlag -->
      <DataSection title="Amorteringsunderlag">
        <DataField label="Amorterings- och låneutrymmesgrundande värdering">
          {{ formatAmountWithCurrency(debt.amortizationInformation.agv) }}
        </DataField>
        <DataField label="Datum för amorterings- och låneutrymmesgrundande värde">
          {{ formatDateIfValid(debt.amortizationInformation.agvDate) ?? '-' }}
        </DataField>
        <DataField label="Amorteringsgrundande skuld">
          {{ formatAmountWithCurrency(debt.amortizationInformation.ags) }}
        </DataField>
        <DataField label="Aktuell skuld som omfattas av amorteringskrav">
          {{ formatAmountWithCurrency(debt.amortizationInformation.debtBoundByRequirements) }}
        </DataField>
        <DataField label="varav aktuell skuld som omfattas av amorteringskrav enligt alternativregeln">
          {{ formatAmountWithCurrency(debt.amortizationInformation.debtPartOfAlternativeRule) }}
        </DataField>
      </DataSection>

      <!-- Betalning -->
      <DataSection title="Betalning">
        <DataField label="Betaldag i månaden">{{ debt.servicingSettings.payDay }}</DataField>
      </DataSection>

      <DataSection title="ID-nummer">
        <DataField label="Bolånets id (debt id)">
          <span class="debt-id">{{ debt.id }}</span>
        </DataField>
      </DataSection>
    </div>
  </SectionCard>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { SectionCard } from '@staff/ui'
import { formatAmount, formatAmountWithCurrency, formatDateIfValid, formatPercentage } from '@staff/shared'

import type { Debt } from '../debt.types'
import { collateralLabel } from '../debt.utils'
import DataField from '@/components/DataField.vue'
import DataSection from '@/components/DataSection.vue'
import {
  effectiveAmortizationPerPeriod,
  pendingAmortizationNote,
  pendingAmortizationPerPeriod,
} from '@/utils/amortizationDisplay'

const { t } = useI18n()

const props = defineProps<{
  debt: Debt
}>()

const totalAmortization = computed(() => {
  return props.debt.loans.reduce((sum, loan) => sum + (loan.loanTerms.amortizationPerPeriod ?? 0), 0)
})

// Only shown when at least one loan has a change booked; then the total is the effective amount per loan.
const pendingTotalAmortization = computed(() => {
  const hasPendingChange = props.debt.loans.some((loan) => pendingAmortizationPerPeriod(loan) !== null)
  if (!hasPendingChange) return null

  return props.debt.loans.reduce((sum, loan) => sum + effectiveAmortizationPerPeriod(loan), 0)
})

const amortizationIsPaused = computed(() => {
  const pauseAmortizationUntil = props.debt.servicingSettings.pauseAmortizationUntil
  if (!pauseAmortizationUntil) return false
  const pauseDate = new Date(pauseAmortizationUntil)
  return !Number.isNaN(pauseDate.getTime()) && pauseDate.getTime() > Date.now()
})

const fullAddress = computed(() => {
  const addr = props.debt.collateralProperty.address
  const street = addr.streetNo ? `${addr.street} ${addr.streetNo}` : addr.street
  const streetPart = addr.apartmentNumber ? `${street} Lgh ${addr.apartmentNumber}` : street
  const cityPart = addr.zip ? `${addr.zip} ${addr.city}` : addr.city
  return `${streetPart}, ${cityPart}`
})

const energyDeclarationLabel = computed(() => {
  const ed = props.debt.collateralProperty.energyDeclaration
  if (!ed) return '-'
  return `${ed.energyClass} (${formatDateIfValid(ed.createdAt)})`
})
</script>

<style scoped lang="scss">
// A value that a pause or a pending change has replaced.
.is-superseded {
  text-decoration-line: line-through;
}

.card-columns {
  display: flex;
  max-width: 100%;
  flex-direction: column;
  gap: var(--space-20);
}

// Cards read better in a narrow column on wide screens; full width below that.
@media (min-width: 80rem) {
  .card-columns {
    max-width: 50%;
  }
}

// One click selects the whole UUID, so it can be copied without dragging across it.
.debt-id {
  user-select: all;
}

.paused-note {
  color: var(--color-success-fg);
}
</style>
